
import React, { createContext, useContext, useEffect } from "react";
import { useSettingsStore } from "@/store/api/settingsStore";

type Currency = "TND" | "EUR" | "SAR";

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatCurrency: (value: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const { settings, updateSettings, fetchSettings } = useSettingsStore();
  
  // Load settings when the component mounts
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const setCurrency = async (currency: Currency) => {
    await updateSettings({
      language: {
        defaultLanguage: settings.language.defaultLanguage,
        currency
      }
    });
  };

  const formatCurrency = (value: number): string => {
    const cur = settings.language.currency;
    const locale = cur === "TND" ? "fr-TN" : cur === "SAR" ? "fr-SA" : "fr-FR";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: cur,
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <CurrencyContext.Provider value={{ 
      currency: settings.language.currency, 
      setCurrency, 
      formatCurrency 
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
}
